import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

const CohortCreate = () => {
  const location = useLocation();
  const [organizationId, setOrganizationId] = useState(location.state?.organizationId || '');
  const [organizations, setOrganizations] = useState([]);
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    const fetchOrganizations = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/organizations', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setOrganizations(response.data);
      } catch (error) {
        console.error('Error fetching organizations:', error.response?.data?.error);
      }
    };
    const currentDate = new Date().toISOString().split('T')[0];
    setStartDate(currentDate);
    fetchOrganizations();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
  
    try {
      console.log('Creating cohort with data:', {
        organizationId,
        name,
        startDate,
        endDate
      });
  
      if (!organizationId) {
        setMessage('Please select an organization.');
        setMessageType('error');
        return;
      }
      if (!name) {
        setMessage('Please enter a cohort name.');
        setMessageType('error');
        return;
      }
      if (!startDate) {
        setMessage('Please select a start date.');
        setMessageType('error');
        return;
      }
      if (!endDate) {
        setMessage('Please select an end date.');
        setMessageType('error');
        return;
      }
  
      const today = new Date();
      const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
      
      const start = new Date(startDate);
      const startUTC = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
      
      const end = new Date(endDate);
      const endUTC = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  
      if (startUTC < todayUTC) {
        setMessage('Start date cannot be in the past.');
        setMessageType('error');
        return;
      }
  
      if (startUTC > endUTC) {
        setMessage('Start date cannot be after end date.');
        setMessageType('error');
        return;
      }
  
      const response = await axios.post(
        'https://genz-staging.feeltiptop.com/api/social_glass/cohort', 
        {
          organization_id: organizationId,
          name,
          start_date: startDate,
          end_date: endDate
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
  
      setMessage(response.data.message);
      setMessageType('success');
      setName('');
      setStartDate('');
      setEndDate('');
    } catch (error) {
      setMessage(error.response?.data?.error || 'Error creating cohort');
      setMessageType('error');
    }
  };  

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="w-full max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-8 py-8 sm:p-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Create Cohort</h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="form-group">
              <label htmlFor="organization" className="block text-base font-medium text-gray-700 mb-2">
                Organization
              </label>
              <select 
                id="organization"
                value={organizationId} 
                onChange={(e) => setOrganizationId(e.target.value)} 
                required
                className="block w-full rounded-md border-gray-300 shadow-sm py-3 px-4 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
              >
                <option value="">Select Organization</option>
                {organizations.map(org => (
                  <option key={org.OrganizationID} value={org.OrganizationID}>
                    {org.Name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="cohortName" className="block text-base font-medium text-gray-700 mb-2">
                Cohort Name
              </label>
              <input 
                id="cohortName"
                type="text" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                required 
                className="block w-full rounded-md border-gray-300 shadow-sm py-3 px-4 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                placeholder="Enter cohort name"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="form-group">
                <label htmlFor="startDate" className="block text-base font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input 
                  id="startDate"
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)} 
                  className="block w-full rounded-md border-gray-300 shadow-sm py-3 px-4 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="endDate" className="block text-base font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input 
                  id="endDate"
                  type="date" 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)} 
                  className="block w-full rounded-md border-gray-300 shadow-sm py-3 px-4 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                />
              </div>
            </div>
            
            <div className="pt-4">
              <button 
                type="submit"
                className="w-full flex justify-center py-3 px-6 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out"
              >
                Create Cohort
              </button>
            </div>
          </form>
          
          {message && (
            <div className={`mt-6 p-4 rounded-md text-base 
              ${messageType === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CohortCreate;